import React, { useState, useEffect } from 'react';
import { MessageSquare, Search, CheckCircle, XCircle, Edit, Trash2, AlertCircle, Eye } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { Testimonial, fetchAllTestimonials, approveTestimonial, rejectTestimonial, updateTestimonial, deleteTestimonial, getFormattedStatus } from '../../lib/testimonials';
import TestimonialForm from '../../components/TestimonialForm';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'pendente' | 'aprovado' | 'rejeitado' | null>('pendente');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<Testimonial | null>(null);
  const [viewingTestimonial, setViewingTestimonial] = useState<Testimonial | null>(null);
  
  const perPage = 10;

  useEffect(() => {
    fetchTestimonials();
  }, [currentPage, selectedStatus]);

  async function fetchTestimonials() {
    try {
      setLoading(true);
      setError('');
      
      const { data, count } = await fetchAllTestimonials({
        status: selectedStatus || undefined,
        page: currentPage,
        perPage,
        searchQuery: searchTerm
      });
      
      setTestimonials(data);
      setTotalCount(count);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar testemunhos');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateTestimonial(testimonialData: any) {
    if (!editingTestimonial) return;
    
    try {
      setError('');
      
      const updatedTestimonial = await updateTestimonial(editingTestimonial.id, testimonialData);
      
      if (!updatedTestimonial) throw new Error('Erro ao atualizar testemunho');
      
      setSuccess('Testemunho atualizado com sucesso!');
      setIsFormOpen(false);
      setEditingTestimonial(undefined);
      fetchTestimonials();
    } catch (err: any) {
      setError(err.message || 'Erro ao atualizar testemunho');
    }
  }

  async function handleApproveTestimonial(id: string) {
    try {
      setError('');
      
      const success = await approveTestimonial(id);
      
      if (!success) throw new Error('Erro ao aprovar testemunho');
      
      setSuccess('Testemunho aprovado com sucesso!');
      fetchTestimonials();
    } catch (err: any) {
      setError(err.message || 'Erro ao aprovar testemunho');
    }
  }

  async function handleRejectTestimonial(id: string) {
    try {
      setError('');
      
      const success = await rejectTestimonial(id);
      
      if (!success) throw new Error('Erro ao rejeitar testemunho');
      
      setSuccess('Testemunho rejeitado com sucesso!');
      fetchTestimonials();
    } catch (err: any) {
      setError(err.message || 'Erro ao rejeitar testemunho');
    }
  }

  async function handleDeleteTestimonial() {
    if (!testimonialToDelete) return;
    
    try {
      setError('');
      
      const success = await deleteTestimonial(testimonialToDelete.id);
      
      if (!success) throw new Error('Erro ao excluir testemunho');
      
      setSuccess('Testemunho excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setTestimonialToDelete(null);
      fetchTestimonials();
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir testemunho');
    }
  }

  function handleEdit(testimonial: Testimonial) {
    setEditingTestimonial(testimonial);
    setIsFormOpen(true);
  }

  function handleCloseForm() {
    setIsFormOpen(false);
    setEditingTestimonial(undefined);
  }

  function confirmDelete(testimonial: Testimonial) {
    setTestimonialToDelete(testimonial);
    setIsDeleteModalOpen(true);
  }

  function viewTestimonial(testimonial: Testimonial) {
    setViewingTestimonial(testimonial);
  }

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <AdminLayout title="Gerenciar Testemunhos">
      {isFormOpen ? (
        <TestimonialForm
          initialData={editingTestimonial}
          onSubmit={handleUpdateTestimonial}
          onCancel={handleCloseForm}
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-brand-teal flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Testemunhos
            </h2>

            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar testemunhos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      fetchTestimonials();
                    }
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                />
              </div>

              <select
                value={selectedStatus || ''}
                onChange={(e) => {
                  setSelectedStatus(e.target.value as any || null);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
              >
                <option value="pendente">Pendentes</option>
                <option value="aprovado">Aprovados</option>
                <option value="rejeitado">Rejeitados</option>
                <option value="">Todos</option>
              </select>

              <button
                onClick={fetchTestimonials}
                className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
              >
                Buscar
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
              <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Título</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Data de Envio</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Data de Aprovação</th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-500">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
                      </div>
                    </td>
                  </tr>
                ) : testimonials.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      Nenhum testemunho encontrado
                    </td>
                  </tr>
                ) : (
                  testimonials.map((testimonial) => (
                    <tr key={testimonial.id} className="border-b border-gray-200">
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-gray-900">{testimonial.title}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{testimonial.content}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          testimonial.status === 'aprovado'
                            ? 'bg-green-100 text-green-800'
                            : testimonial.status === 'rejeitado'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {getFormattedStatus(testimonial.status)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-500">
                        {format(new Date(testimonial.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                      </td>
                      <td className="px-4 py-4 text-gray-500">
                        {testimonial.approved_at 
                          ? format(new Date(testimonial.approved_at), 'dd/MM/yyyy', { locale: ptBR })
                          : '-'}
                      </td>
                      <td className="px-4 py-4 text-right space-x-1">
                        {testimonial.status === 'pendente' && (
                          <>
                            <button
                              onClick={() => handleApproveTestimonial(testimonial.id)}
                              className="text-green-500 hover:text-green-600 p-1"
                              title="Aprovar"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRejectTestimonial(testimonial.id)}
                              className="text-red-500 hover:text-red-600 p-1"
                              title="Rejeitar"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleEdit(testimonial)}
                          className="text-brand-teal hover:text-brand-teal/80 p-1"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => confirmDelete(testimonial)}
                          className="text-red-500 hover:text-red-600 p-1"
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => viewTestimonial(testimonial)}
                          className="text-gray-500 hover:text-gray-700 p-1"
                          title="Visualizar"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                >
                  Anterior
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-full ${
                      currentPage === page
                        ? 'bg-brand-teal text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded-lg border border-gray-300 disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {isDeleteModalOpen && testimonialToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o testemunho <strong>"{testimonialToDelete.title}"</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteTestimonial}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de visualização de testemunho */}
      {viewingTestimonial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-brand-teal">{viewingTestimonial.title}</h3>
              <button
                onClick={() => setViewingTestimonial(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-sm px-2 py-1 rounded-full ${
                  viewingTestimonial.status === 'aprovado'
                    ? 'bg-green-100 text-green-800'
                    : viewingTestimonial.status === 'rejeitado'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {getFormattedStatus(viewingTestimonial.status)}
                </span>
                <span className="text-sm text-gray-500">
                  Enviado em {format(new Date(viewingTestimonial.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                </span>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-line">
                {viewingTestimonial.content}
              </div>
            </div>
            
            {viewingTestimonial.status === 'pendente' && (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => {
                    handleApproveTestimonial(viewingTestimonial.id);
                    setViewingTestimonial(null);
                  }}
                  className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                >
                  <CheckCircle className="h-4 w-4" />
                  Aprovar
                </button>
                <button
                  onClick={() => {
                    handleRejectTestimonial(viewingTestimonial.id);
                    setViewingTestimonial(null);
                  }}
                  className="flex-1 bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Rejeitar
                </button>
              </div>
            )}
            
            <div className="flex justify-end">
              <button
                onClick={() => setViewingTestimonial(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}